<section
  class="flex flex-col gap-6"
  data-testid="ai-scribe-upload-tool"
>
  <header class="flex flex-col gap-1">
    <p class="text-medium font-bold">Upload Recording</p>
    <p class="mat-caption !mb-0">
      Upload audio recordings to generate Scribe Notes.
    </p>
  </header>

  @let state = viewState();

  <!-- Shared hidden file input; the dropzones trigger it via #fileInput. -->
  <input
    #fileInput
    type="file"
    class="hidden"
    accept=".mp3,.wav,.m4a,audio/*"
    data-testid="upload-file-input"
    (change)="fileSelected($event)"
  />

  @if (state.kind === 'failed') {
    <div
      class="border-warn-500/30 bg-warn-500/5 rounded-xl border-2 p-6"
      data-testid="upload-error"
    >
      <div class="flex items-start gap-4">
        <div
          class="bg-warn-500/10 text-warn-500 flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
        >
          <mat-icon>error</mat-icon>
        </div>
        <div class="flex-1">
          <p class="text-warn-500 !mb-0 text-sm font-semibold">Upload failed</p>
          <p class="!mb-0 mt-1 text-sm text-slate-600">{{ state.message }}</p>
          @if (state.file; as file) {
            <p class="!mb-0 mt-1 text-xs text-slate-400">
              {{ file.name }} · {{ file.sizeLabel }} · stopped at
              {{ state.progress }}%
            </p>
          }
          <div class="mt-4 flex gap-3">
            <button
              type="button"
              mat-flat-button
              color="warn"
              data-testid="upload-retry"
              [disabled]="canStart() === false"
              (click)="startUpload()"
            >
              <mat-icon>refresh</mat-icon>
              Retry
            </button>
            <button type="button" mat-stroked-button (click)="reset()">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  } @else if (state.kind === 'rejected') {
    <div
      role="button"
      tabindex="0"
      class="border-warn-500/50 bg-warn-500/5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center"
      data-testid="upload-rejected"
      (click)="fileInput.click()"
      (keydown.enter)="fileInput.click()"
      (dragover)="dragOver($event)"
      (dragleave)="dragLeave()"
      (drop)="fileDropped($event)"
    >
      <div
        class="bg-warn-500/10 text-warn-500 mb-3 flex h-14 w-14 items-center justify-center rounded-full"
      >
        <mat-icon>warning</mat-icon>
      </div>
      @if (state.rejection.variant === 'type') {
        <p class="text-warn-500 !mb-0 text-sm font-semibold">
          File type not supported
        </p>
        <p class="!mb-0 mt-1 text-sm text-slate-600">
          Please upload an .mp3, .wav, or .m4a file.
        </p>
        <p class="!mb-0 mt-2 text-xs text-slate-400">
          Rejected: {{ state.rejection.fileName }}
        </p>
      } @else {
        <p class="text-warn-500 !mb-0 text-sm font-semibold">
          File exceeds 500 MB limit
        </p>
        <p class="!mb-0 mt-1 text-sm text-slate-600">
          Please upload a recording smaller than 500 MB.
        </p>
        <p class="!mb-0 mt-2 text-xs text-slate-400">
          Rejected: {{ state.rejection.fileName
          }}@if (state.rejection.sizeLabel; as sizeLabel) {
            · {{ sizeLabel }}
          }
        </p>
      }
    </div>
  } @else {
    <div class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      @switch (state.kind) {
        @case ('idle') {
          <div class="flex flex-col gap-5" data-testid="upload-idle">
            <label class="flex flex-col gap-1.5">
              <span class="text-accent-500 text-sm font-medium">Patient</span>
              <pr-patient-selector
                class="block"
                placeholder="Patient"
                [formControl]="patientControl"
              />
            </label>

            <div
              role="button"
              tabindex="0"
              class="bg-pr-background hover:border-primary-500 hover:bg-primary-50 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors"
              data-testid="upload-dropzone"
              [ngClass]="
                dragActive() ? 'border-primary-500 bg-primary-50' : 'border-slate-300'
              "
              (click)="fileInput.click()"
              (keydown.enter)="fileInput.click()"
              (dragover)="dragOver($event)"
              (dragleave)="dragLeave()"
              (drop)="fileDropped($event)"
            >
              <div
                class="bg-primary-50 text-primary-500 mb-3 flex h-14 w-14 items-center justify-center rounded-full"
              >
                <mat-icon>cloud_upload</mat-icon>
              </div>
              <p class="text-accent-500 !mb-0 text-sm font-medium">
                Drop an audio file here, or
                <span class="text-primary-500 underline">click to browse</span>
              </p>
              <p class="!mb-0 mt-1 text-xs text-slate-400">
                Supported formats: .mp3, .wav, .m4a — max 500 MB
              </p>
            </div>
          </div>
        }

        @case ('file-selected') {
          <div class="flex flex-col gap-5" data-testid="upload-file-selected">
            @if (selectedPatient(); as patient) {
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="text-slate-400">Patient:</span>
                  <span class="text-accent-500 font-medium">{{
                    patient.name
                  }}</span>
                </div>
                @if (practitionerName(); as practitioner) {
                  <div>
                    <span class="text-slate-400">Practitioner:</span>
                    <span class="text-accent-500 font-medium">{{
                      practitioner
                    }}</span>
                  </div>
                }
              </div>
            } @else {
              <label class="flex flex-col gap-1.5">
                <span class="text-accent-500 text-sm font-medium">Patient</span>
                <pr-patient-selector
                  class="block"
                  placeholder="Patient"
                  [formControl]="patientControl"
                />
              </label>
            }

            <div
              class="bg-pr-background flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3.5"
            >
              <div class="flex items-center gap-3">
                <div
                  class="bg-primary-50 text-primary-500 flex h-10 w-10 items-center justify-center rounded-lg"
                >
                  <mat-icon>audiotrack</mat-icon>
                </div>
                <div>
                  <p class="text-accent-500 !mb-0 text-sm font-medium">
                    {{ state.file.name }}
                  </p>
                  <p class="!mb-0 text-xs text-slate-400">
                    {{ state.file.sizeLabel }} · Ready to upload
                  </p>
                </div>
              </div>
              <button
                type="button"
                class="hover:text-warn-500 flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                aria-label="Remove file"
                (click)="clearFile()"
              >
                <mat-icon class="icon-xs">close</mat-icon>
                Remove
              </button>
            </div>

            <div class="flex justify-end gap-3">
              <button type="button" mat-stroked-button (click)="clearFile()">
                Cancel
              </button>
              <button
                type="button"
                mat-flat-button
                color="primary"
                data-testid="upload-start"
                [disabled]="canStart() === false"
                (click)="startUpload()"
              >
                <mat-icon>cloud_upload</mat-icon>
                Start Upload
              </button>
            </div>
          </div>
        }

        @case ('uploading') {
          <div class="flex flex-col gap-5" data-testid="upload-progress">
            <div class="flex items-center gap-3">
              <div
                class="bg-primary-50 text-primary-500 flex h-10 w-10 items-center justify-center rounded-lg"
              >
                <mat-icon>audiotrack</mat-icon>
              </div>
              <div>
                <p class="text-accent-500 !mb-0 text-sm font-medium">
                  {{ state.file.name }}
                </p>
                <p class="!mb-0 text-xs text-slate-400">
                  {{ state.file.sizeLabel
                  }}@if (selectedPatient(); as patient) {
                    · {{ patient.name }}
                  }@if (practitionerName(); as practitioner) {
                    · {{ practitioner }}
                  }
                </p>
              </div>
            </div>

            <div>
              <div class="mb-1.5 flex items-center justify-between text-sm">
                <span class="text-accent-500 font-medium">
                  @if (state.preparing) {
                    Preparing upload…
                  } @else {
                    Uploading… {{ state.progress }}%
                  }
                </span>
                @if (state.preparing === false) {
                  <span class="text-xs text-slate-400">
                    {{ state.loadedLabel }} of {{ state.file.sizeLabel }}
                  </span>
                }
              </div>
              <mat-progress-bar
                [mode]="state.preparing ? 'indeterminate' : 'determinate'"
                [value]="state.progress"
              />
            </div>
          </div>
        }

        @case ('processing') {
          <div class="flex flex-col gap-5" data-testid="upload-processing">
            <div class="flex items-center gap-4">
              <div
                class="bg-primary-50 text-primary-500 flex h-12 w-12 items-center justify-center rounded-full"
              >
                <mat-icon class="animate-spin">autorenew</mat-icon>
              </div>
              <div class="flex-1">
                <p
                  class="text-accent-500 !mb-0 flex items-center gap-2 text-sm font-medium"
                >
                  Finishing notes…
                  <span
                    class="bg-primary-500 inline-flex h-2 w-2 animate-pulse rounded-full"
                  ></span>
                </p>
                <p class="!mb-0 mt-0.5 text-xs text-slate-400">
                  Audio uploaded successfully. Generating Scribe Notes, this
                  usually takes under a minute.
                </p>
              </div>
              <span
                class="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500"
              >
                <mat-icon class="icon-xs text-success-500">check</mat-icon>
                Upload complete
              </span>
            </div>
            <mat-progress-bar mode="indeterminate" />
          </div>
        }

        @case ('streaming') {
          <div class="flex flex-col gap-4" data-testid="upload-streaming">
            <div class="flex items-center justify-between">
              <p class="text-accent-500 !mb-0 text-sm font-medium">
                {{ selectedFile()?.name
                }}@if (selectedPatient(); as patient) {
                  · {{ patient.name }}
                }@if (practitionerName(); as practitioner) {
                  · {{ practitioner }}
                }
              </p>
              <span
                class="bg-primary-50 text-primary-500 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
              >
                <span
                  class="bg-primary-500 inline-flex h-2 w-2 animate-pulse rounded-full"
                ></span>
                Generating notes…
              </span>
            </div>

            <ul class="flex flex-col gap-3">
              @for (note of state.notes; track note.id) {
                <li
                  class="rounded-lg border p-4"
                  [ngClass]="
                    note.status === 'ready'
                      ? 'border-slate-200 bg-white'
                      : 'border-primary-100 bg-primary-50/40'
                  "
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      @if (note.status === 'ready') {
                        <mat-icon class="icon-xs text-success-500"
                          >check_circle</mat-icon
                        >
                      } @else {
                        <mat-icon class="icon-xs text-primary-500 animate-spin"
                          >autorenew</mat-icon
                        >
                      }
                      <span class="text-accent-500 text-sm font-medium">{{
                        note.name
                      }}</span>
                    </div>
                    @if (note.status === 'ready') {
                      <span class="text-success-500 text-xs">Complete</span>
                    } @else {
                      <span
                        class="text-primary-500 flex items-center gap-1 text-xs"
                      >
                        <span
                          class="bg-primary-500 inline-flex h-1.5 w-1.5 animate-pulse rounded-full"
                        ></span>
                        Generating…
                      </span>
                    }
                  </div>
                  @if (note.content) {
                    <p class="!mb-0 mt-2 text-sm text-slate-600">
                      {{ note.content }}
                    </p>
                  }
                </li>
              }
            </ul>
          </div>
        }

        @case ('completed') {
          <div class="flex flex-col gap-4" data-testid="upload-success">
            <div class="flex items-center justify-between">
              <p class="text-accent-500 !mb-0 text-sm font-medium">
                {{ selectedFile()?.name
                }}@if (selectedPatient(); as patient) {
                  · {{ patient.name }}
                }@if (practitionerName(); as practitioner) {
                  · {{ practitioner }}
                }
              </p>
              <span
                class="bg-success-500/10 text-success-500 ring-success-500/30 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset"
              >
                <mat-icon class="icon-xs">check</mat-icon>
                Ready
              </span>
            </div>

            @if (state.notes.length > 0) {
              <ul class="flex flex-col gap-3">
                @for (note of state.notes; track note.id) {
                  <li
                    class="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm"
                  >
                    <div class="flex items-center gap-3">
                      <div
                        class="bg-success-500/10 text-success-500 flex h-9 w-9 items-center justify-center rounded-lg"
                      >
                        <mat-icon class="icon-xs">check_circle</mat-icon>
                      </div>
                      <div>
                        <p class="text-accent-500 !mb-0 text-sm font-medium">
                          {{ note.name }}
                        </p>
                        <p class="!mb-0 text-xs text-slate-400">
                          Scribe Note ready for review
                        </p>
                      </div>
                    </div>
                    <div class="flex items-center gap-1">
                      <button
                        type="button"
                        class="text-primary-500 hover:bg-primary-50 flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium"
                        data-testid="view-scribe-note"
                        (click)="viewNote(note)"
                      >
                        View Scribe Note
                        <mat-icon class="icon-xs">arrow_forward</mat-icon>
                      </button>
                      @if (firestoreLink()) {
                        <button
                          type="button"
                          class="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100"
                          data-testid="view-in-firestore"
                          (click)="viewInFirestore()"
                        >
                          View in Firestore
                          <mat-icon class="icon-xs">open_in_new</mat-icon>
                        </button>
                      }
                    </div>
                  </li>
                }
              </ul>
            }

            <div class="flex justify-end">
              <button type="button" mat-stroked-button (click)="reset()">
                <mat-icon>cloud_upload</mat-icon>
                Upload another recording
              </button>
            </div>
          </div>
        }
      }
    </div>
  }
</section>
